import {
  EllipsisOutlined,
  EyeOutlined,
  KeyOutlined,
  LeftOutlined,
  MinusOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  ScheduleOutlined,
  SolutionOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Dropdown,
  Flex,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Skeleton,
  Spin,
  Steps,
  Switch,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import { DefaultOptionType } from "antd/lib/select";
import dayjs from "dayjs";
import { LocalStorage } from "lowdb/browser";
import { CSSProperties, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { removeNotification } from "src/actions/session.action";
import { AdmissionApi, DocumentApi, EmployeeApi, ScheduleApi, TimeRuleApi, WorkplaceApi } from "src/api";
import { default as WebappEmployeeApi } from "src/api/webapp/employee-api";
import { FirstAccessFlowDocumentation, LogoColoridaSimples } from "src/assets/images";
import { InputGroup, Option, Select } from "src/components";
import { REACT_APP_BASE_PATH } from "src/constants";
import { SocketContext } from "src/contexts";
import { useChat } from "src/contexts/chat.context";
import { useGlobalContext } from "src/contexts/global.context";
import { useSessionContext } from "src/contexts/session.context";
import {
  activityNature,
  admissionIndicator,
  admissionType,
  countryOfBirth,
  countryOfNationality,
  countryStates,
  educationLevel,
  genders,
  jobOccupations,
  journeyRegime,
  laborRegime,
  legalHypothesisTemporaryWorker,
  monthProfessionalCategory,
  partTimeContract,
  paymentUnitRemuneration,
  pensionRegime,
  races,
  registrationType,
  typeEmploymentContract,
  typeEvent,
  typeOfWorkingDay,
  workerCategory,
} from "src/data";
import { ChatOption } from "src/modules/ai-agent/enums";
import MaskedInput from "src/modules/CustomMaskedInput";
import { DocumentMenuDropdownProp } from "src/pages/Private/Registration/EmployeeEdit/TabDocuments";
import { ExpiredSessionError, capitalizeFirstLetter, dictionary, i18n, numericRegExp } from "src/utils";
import { v4 } from "uuid";
import GoodErrorMessage from "../Modal/GoodErrorMessage";
import GoodInfoMessage from "../Modal/GoodInfoMessage";
import { Data, LowSyncWithLodash } from "../NotificationsList";

const { Title } = Typography;
const { Item } = Form;
const defaultColor = "#884EA6";
type ProgressStatuses = "normal" | "exception" | "active" | "success";

const inputNumberControls = { upIcon: <PlusOutlined />, downIcon: <MinusOutlined /> };

const titleStyle: CSSProperties = {
  margin: 0,
  color: defaultColor,
};

const titleStyle2: CSSProperties = {
  marginTop: 8,
  color: defaultColor,
};

type Step = 1 | 2 | 3 | 4;

const progressObject = [
  { percent: 50, status: "active", color: defaultColor, type: undefined },
  { percent: 100, status: "exception", color: undefined, type: "danger" },
  { percent: 100, status: "success", color: undefined, type: "success" },
];

type DoneAdmissionModalProps = {
  open: boolean;
  onCancel: () => void;
  employeeId: number;
  employerId: string;
  userId: number;
  sentToEsocial: boolean;
  admissionFlow: boolean;
  notifyResult: (response: any) => void;
};

export default function DoneAdmissionModal({
  open,
  onCancel,
  employeeId,
  employerId,
  userId,
  sentToEsocial,
  admissionFlow,
  notifyResult,
}: DoneAdmissionModalProps) {
  const { isYappUser } = useGlobalContext();

  const { setOption } = useChat();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [employee, setEmployee] = useState<Employee>();

  const [sendToEsocial, setSendToEsocial] = useState(false);
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);

  // Step 1
  const [formEsocial] = Form.useForm();
  const employmentRegimeId = Form.useWatch("employmentRegimeId", formEsocial);
  const [hasSalaryDescription, setHasSalaryDescription] = useState(false);
  const [isPositionJobCumulative, setIsPositionJobCumulative] = useState(false);
  const [isTemporaryCollaborator, setIsTemporaryCollaborator] = useState(false);
  const [isTpContrDay, setIsTpContrDay] = useState(false);
  const [isTpContrFact, setIsTpContrFact] = useState(false);
  const [hasAssuranceClause, setHasAssuranceClause] = useState(false);
  const [hasWeeklyShiftNighttime, setHasWeeklyShiftNighttime] = useState(false);
  const [timeRules, setTimeRules] = useState<Department[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [pinExists, setPinExists] = useState(false);
  const [gettingPin, setGettingPin] = useState(false);
  const [zipCode, setZipCode] = useState<string>(employee?.address?.zipCode ?? "");
  const [cpf, setCpf] = useState<string>(employee?.cpf ?? "");

  // Step 2
  const [formCertificatePassword] = Form.useForm();

  // Step 3
  const [formEsocialMonitoring] = Form.useForm();
  const [progressIndex, setProgressIndex] = useState(0);
  const [errorContent, setErrorContent] = useState("");

  // Step 4
  const [formTimesheetConfig] = Form.useForm();
  const [pin, setPin] = useState("");
  const [finishing, setFinishing] = useState(false);

  const typeEmploymentContractOnchange = (value: number) => {
    switch (value) {
      case 2:
        setIsTpContrDay(true);
        setIsTpContrFact(false);
        break;
      case 3:
        setIsTpContrFact(true);
        setIsTpContrDay(false);
        break;
      default:
        setIsTpContrFact(false);
        setIsTpContrDay(false);
    }
  };

  function next() {
    if (sendToEsocial) {
      setStep((prevStep) => (prevStep + 1) as Step);
    } else {
      setStep(4);
    }
  }

  const getDocument = (documentCode: string) =>
    `${REACT_APP_BASE_PATH}/download/${
      employee?.documentAttachments.find((attach) => attach.document?.code == documentCode)?.attachment ?? ""
    }`;

  const [urlUploadedFileIdentity, setUrlUploadedFileIdentity] = useState<string>(getDocument("RG"));
  const [urlUploadedFileCpf, setUrlUploadedFileCpf] = useState<string>(getDocument("CPF"));
  const [urlUploadedFilePis, setUrlUploadedFilePis] = useState<string>(getDocument("PIS"));
  const [urlUploadedFileCt, setUrlUploadedFileCt] = useState<string>(getDocument("CT"));
  const [urlUploadedFileTe, setUrlUploadedFileTe] = useState<string>(getDocument("TE"));
  const [urlUploadedFileCnh, setUrlUploadedFileCnh] = useState<string>(getDocument("CNH"));
  const [urlUploadedFileNr, setUrlUploadedFileNr] = useState<string>(getDocument("NR"));
  const [urlUploadedFileCMEST, setUrlUploadedFileCMEST] = useState<string>(getDocument("CMEST"));
  const [urlUploadedFileTce, setUrlUploadedFileTce] = useState<string>(getDocument("TCE"));
  const [urlUploadedFileCe, setUrlUploadedFileCe] = useState<string>(getDocument("CE"));

  function DocumentMenuDropdown(props: DocumentMenuDropdownProp): JSX.Element {
    const [file, setFile] = useState("");

    function getBase64(img: any, callback: (result: any) => void) {
      const reader = new FileReader();
      reader.addEventListener("load", () => callback(reader.result));
      reader.readAsDataURL(img);
    }

    const bytesToMb = (bytes: number) => bytes / 1000000;

    const fileIsTooLarge = (file: any) => bytesToMb(file.size) > 1;

    const fileIsImage = (file: any) => file.type.startsWith("image/");

    // Atualiza a url do documento de acordo com o seu respectivo código
    function updateUrlUpload(documentCode: string, url: string) {
      const urlBasePath = `${REACT_APP_BASE_PATH}/download/`;

      const urlSetters: Record<string, React.Dispatch<React.SetStateAction<string>>> = {
        RG: setUrlUploadedFileIdentity,
        CPF: setUrlUploadedFileCpf,
        PIS: setUrlUploadedFilePis,
        CT: setUrlUploadedFileCt,
        TE: setUrlUploadedFileTe,
        CNH: setUrlUploadedFileCnh,
        NR: setUrlUploadedFileNr,
        CMEST: setUrlUploadedFileCMEST,
        TCE: setUrlUploadedFileTce,
        CE: setUrlUploadedFileCe,
      };

      const setter = urlSetters[documentCode];
      if (setter) setter(urlBasePath + url);
    }

    async function handleUpload(info: any, documentCode: string) {
      if (info.file.percent == 100) {
        if (!fileIsImage(info.file)) {
          Modal.warning({
            title: i18n(dictionary.error.ATTACH_DOCUMENT_ERROR_IMAGE.title),
            content: i18n(dictionary.error.ATTACH_DOCUMENT_ERROR_IMAGE.message),
          });
          return;
        }

        if (fileIsTooLarge(info.file)) {
          Modal.warning({
            title: i18n(dictionary.error.ATTACH_DOCUMENT_ERROR_SIZE.title),
            content: i18n(dictionary.error.ATTACH_DOCUMENT_ERROR_SIZE.message),
          });
          return;
        }

        getBase64(info.file.originFileObj, async (fileURL) => {
          const fileBuffer = fileURL.replace(/(data:image\/(jpeg)*(gif)*(png)*\;(base64),)/g, "");
          setFile(fileBuffer);
          try {
            const res = await DocumentApi.uploadFile(info.file.originFileObj, documentCode, props.employeeId);
            updateUrlUpload(documentCode, res.url);
            GoodInfoMessage({
              title: i18n(dictionary.success.ATTACH_DOCUMENT_SUCCESS.title),
              message: i18n(dictionary.success.ATTACH_DOCUMENT_SUCCESS.message),
            });
          } catch (error) {
            if (!(error instanceof ExpiredSessionError)) {
              GoodErrorMessage({
                title: i18n(dictionary.error.ATTACH_DOCUMENT_ERROR.title),
                message: i18n(dictionary.error.ATTACH_DOCUMENT_ERROR.message),
                onClickLink: () => setOption(ChatOption.CHAT),
                onOk: () => handleUpload(info, documentCode),
              });
            }
            console.error(error);
          }
        });
      }
    }

    const documentMenuItems = [
      {
        icon: <EyeOutlined />,
        label: i18n(dictionary.action.view),
        key: "1",
        onClick: () => {
          Modal.info({
            title: "Visualização",
            content: (
              <Image
                src={props.url}
                fallback={
                  "data:image/png;base64,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"
                }
                placeholder={<Spin />}
              />
            ),
          });
        },
      },
      {
        label: (
          <Item
            name="identityAttachment"
            noStyle
          >
            <Upload
              accept={"image/*"}
              showUploadList={false}
              multiple={false}
              onChange={async (info) => await handleUpload(info, props.code)}
            >
              <UploadOutlined /> {i18n(dictionary.action.add_document)}
            </Upload>
          </Item>
        ),
        key: "2",
      },
    ];

    return (
      <Dropdown
        menu={{ items: documentMenuItems }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <EllipsisOutlined />
      </Dropdown>
    );
  }

  function localDebounce<T extends (...args: any[]) => void>(func: T, delay: number): (...args: Parameters<T>) => void {
    let timer: ReturnType<typeof setTimeout>;

    return (...args: Parameters<T>) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  }

  async function handleCancel() {
    onCancel();
    setStep(1);
    formEsocial.resetFields();
  }

  async function gerarPin() {
    setGettingPin(true);

    let generatedPin = "";
    let exists = true;
    while (exists) {
      generatedPin = "";
      while (generatedPin.length < 5) {
        generatedPin += Math.floor(Math.random() * 10);
      }
      exists = await checkPin(generatedPin);
    }
    setPin(generatedPin);
    formTimesheetConfig.setFieldsValue({ pin: generatedPin });
    setGettingPin(false);
  }

  /**
   * Checa se o pin já está associado a outro colaborador da empresa ou não.
   * @param {number} pin Código do PIN.
   * @returns {boolean} Resultado da checagem: true se existe, false se não existe.
   */
  async function checkPin(pin: string): Promise<boolean> {
    return await WebappEmployeeApi.checkPin(employeeId, pin)
      .then((res: any) => {
        setPinExists(res.data.exists);
        return res.data.exists;
      })
      .catch((err: any) => {
        console.error(err);
        setPinExists(false);
        return false;
      });
  }

  const [sendEsocialSuccess, setSendEsocialSuccess] = useState(false);
  const navigate = useNavigate();

  async function handleOk() {
    switch (step) {
      case 1: {
        formEsocial.submit();
        const validateFields = await formEsocial
          .validateFields()
          .then((_) => true)
          .catch((_) => false);
        if (validateFields) {
          const updateResult = await onUpdateEmployee({
            employerId,
            ...formEsocial.getFieldsValue(),
          });
          if (updateResult) {
            if (!sendToEsocial && isYappUser) {
              const successAdmission = await finishAdmission(employeeId);
              notifyResult(successAdmission);
              if (successAdmission) {
                onCancel();
              }
            } else {
              next();
            }
          }
        }
        break;
      }
      case 2: {
        const validateFields = await formCertificatePassword
          .validateFields()
          .then((_) => true)
          .catch((_) => false);
        if (validateFields) {
          formCertificatePassword.submit();
          next();
        }

        break;
      }
      case 3: {
        if (sendEsocialSuccess) {
          getInitData();
          if (isYappUser) {
            const successAdmission = await finishAdmission(employeeId);
            notifyResult(successAdmission);
            if (successAdmission) {
              onCancel();
            }
          } else {
            next();
          }
        }
        break;
      }
      case 4: {
        const updateResult = await onUpdateEmployee({
          tab: "2",
          employerId,
          ...formTimesheetConfig.getFieldsValue(),
        });
        if (updateResult) {
          const successAdmission = await finishAdmission(employeeId);
          notifyResult(successAdmission);
          if (successAdmission) {
            onCancel();
          }
        }

        break;
      }
    }
  }

  async function onUpdateEmployee(values: any): Promise<boolean> {
    setLoading(true);
    return EmployeeApi.updateById(employeeId, values)
      .then(async (res) => {
        setLoading(false);
        return true;
      })
      .catch((error) => {
        setLoading(false);
        if (!(error instanceof ExpiredSessionError)) {
          GoodErrorMessage({
            title: i18n(dictionary.error.UPDATE_EMPLOYEE_ERROR.title),
            message: i18n(dictionary.error.UPDATE_EMPLOYEE_ERROR.message),
            onOk: () => onUpdateEmployee(values),
            onClickLink: () => setOption(ChatOption.CHAT),
          });
        }
        return false;
      });
  }

  async function finishAdmission(employeeId: number): Promise<boolean> {
    if (admissionFlow) {
      const response = await AdmissionApi.updateAdmissionFlowStep(employeeId);
      return response;
    } else {
      return AdmissionApi.finish(employeeId)
        .then((result) => {
          if (!result.success) {
            let specificMessage = " ";

            if (result.requiredDocumentErrors.length > 0) {
              i18n(dictionary.info.FINISH_ADMISSION_INFO.specificMessage);
              for (const documentError of result.requiredDocumentErrors) {
                specificMessage += i18n(dictionary.info.FINISH_ADMISSION_INFO.requiredDocument[documentError]) + ", ";
              }

              specificMessage = specificMessage.slice(0, -2) + ".";
            }

            GoodInfoMessage({
              title: i18n(dictionary.info.FINISH_ADMISSION_INFO.title),
              message: i18n(dictionary.info.FINISH_ADMISSION_INFO.message) + specificMessage,
            });

            return false;
          }

          if (sendToEsocial) {
            GoodInfoMessage({
              title: i18n(dictionary.success.FINISH_ADMISSION_SUCCESS.title),
              message: i18n(dictionary.success.FINISH_ADMISSION_SUCCESS.message),
            });
          } else {
            Modal.success({
              title: i18n(dictionary.success.FINISH_ADMISSION_SUCCESS.title),
            });
          }

          return true;
        })
        .catch((error) => {
          if (!(error instanceof ExpiredSessionError)) {
            if (error.response?.data) {
              GoodInfoMessage({
                title: i18n(dictionary.error.FINISH_ADMISSION_ERROR.title),
                message: i18n(dictionary.error.start + error.response?.data + dictionary.error.message),
              });
            } else {
              GoodErrorMessage({
                title: i18n(dictionary.error.FINISH_ADMISSION_ERROR.title),
                message: i18n(dictionary.error.FINISH_ADMISSION_ERROR.message),
                onOk: () => finishAdmission(employeeId),
                onClickLink: () => setOption(ChatOption.CHAT),
              });
            }
          }
          console.error(error);
          return false;
        });
    }
  }

  async function getInitData() {
    if (open) {
      setLoadingData(true);

      // Campos que precisam ser iniciados
      formEsocial.setFieldValue("workloadType", 1);

      // Locais de trabalho
      if (!workplaces.length) {
        await WorkplaceApi.fetch().then((res: Workplace[]) => setWorkplaces(res));
      }
      // Escalas
      if (!schedules.length) {
        await ScheduleApi.fetch().then((res: Schedule[]) => setSchedules(res));
      }
      // Regras de horas
      if (!timeRules.length) {
        await TimeRuleApi.fetch().then((res: TimeRule[]) => setTimeRules(res));
      }

      //Dados do eSocial
      const employeeData = await EmployeeApi.fetchById(employeeId);
      formEsocial.setFieldsValue({
        ...employeeData,
        ...employeeData?.additionalData,
        ...employeeData?.eSocialData,
        birthDate: employeeData?.birthDate ? dayjs(employeeData?.birthDate) : undefined,
        admissionDate: employeeData?.admissionDate ? dayjs(employeeData?.admissionDate) : dayjs(),
        fgtsDate: employeeData?.eSocialData?.fgtsDate ? dayjs(employeeData?.eSocialData.fgtsDate) : undefined,
        endContractDate: employeeData?.eSocialData?.endContractDate
          ? dayjs(employeeData?.eSocialData.endContractDate)
          : undefined,
        driverLicenseExpirationDate: employeeData?.eSocialData?.driverLicenseExpirationDate
          ? dayjs(employeeData?.eSocialData.driverLicenseExpirationDate)
          : undefined,
        assuranceClause: employeeData?.eSocialData?.assuranceClause ?? false,
        benefits: employeeData?.benefits?.map((benefit) => benefit.id),
        ...employeeData?.address,
      });
      setCpf(employeeData?.cpf ?? "");
      setZipCode(employeeData?.address?.zipCode ?? "");
      setEmployee(employeeData);
      setLoadingData(false);
    }
  }

  const adapter = new LocalStorage<Data>("qrpoint-notifications");
  const defaultData: Data = {
    notifications: [],
  };
  const db = new LowSyncWithLodash(adapter, defaultData);
  db.read();
  const socket = useContext(SocketContext);
  const { sessionState, sessionDispatch } = useSessionContext();

  function sendESocialEvent(employeeId: number, certPass: string) {
    // sessionDispatch(loadingNotification(true));
    setProgressIndex(0);
    startProgress();
    setSendEsocialSuccess(false);
    const socketId = v4();
    socket.emit("send-esocial-admission-event-batch", {
      id: socketId,
      params: {
        certPass: certPass,
        employeeId: employeeId,
        userId: userId,
      },
    });

    socket.on(socketId, (data: any) => {
      const notificationCollection = db.data.notifications;
      let title = "Evento de admissão enviado para o eSocial com sucesso!";
      let content =
        "O recibo de processamento do evento estará disponível pelos próximos 20 " +
        "minutos. Clique aqui para fazer o download do recibo!";
      let notifier = "Verifique as suas notificações no sistema ou clique aqui para baixar.";
      let link = REACT_APP_BASE_PATH + decodeURIComponent(data.url);

      if (data.error) {
        title = "Não foi possível enviar o evento de admissão para o eSocial!";
        content = "Ocorreu um erro ao enviar o evento de admissão para o eSocial. Por favor, tente novamente.";
        notifier = "Verifique as suas notificações no sistema.";
        link = "";

        if (data.errorCode) {
          if (data.errorCode === "E_SOCIAL_FIRST_VALIDATION_ERROR") {
            content = data.errorMessage.replace("E_SOCIAL_FIRST_VALIDATION_ERROR: ", "");
          } else {
            content = i18n(dictionary.error.start + data.errorCode + dictionary.error.message);
          }
        }

        // Notifier.focus(title, notifier, link, window.location.origin + "/res/logo-simples.svg");
        setErrorContent(content);
        setProgressIndex(1);
      } else {
        setProgressIndex(2);
        setSendEsocialSuccess(true);
        // Notifier.start(title, notifier, link, window.location.origin + "/res/logo-simples.svg");
      }

      // notificationCollection.push({
      //   userId,
      //   id: socketId,
      //   expires: dayjs().add(20, "minutes").valueOf(),
      //   title,
      //   content,
      //   link,
      // });
      // db.data.notifications = notificationCollection;
      // db.write();

      // sessionDispatch(
      //   addNotification({
      //     userId,
      //     id: socketId,
      //     title,
      //     content,
      //     link,
      //   }),
      // );

      // sessionDispatch(loadingNotification(false));
      socket.removeListener(socketId);
      setTimeout(
        () => {
          sessionDispatch(removeNotification(socketId));
        },
        1000 * 60 * 19,
      ); //20 minutos para deletar a notificação
    });
  }

  const formPassHandleSubmit = (values: any) => {
    sendESocialEvent(employeeId, values.password);
  };

  useEffect(() => {
    getInitData();
  }, [open]);

  useEffect(() => {
    setSendToEsocial(sentToEsocial);
  }, [sentToEsocial]);

  const [percent, setPercent] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const startProgress = () => {
    if (isRunning) {
      // Resetar
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setPercent(0);
      setIsRunning(false);
    } else {
      // Iniciar
      setIsRunning(true);
      intervalRef.current = setInterval(() => {
        setPercent((prev) => {
          if (prev >= 100) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
            setIsRunning(false);
            return 100;
          }
          return prev + 2;
        });
      }, 1000);
    }
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handlePaste = (event: { preventDefault: () => void }) => {
    event.preventDefault();
  };

  let stepsItems = sendToEsocial
    ? [
        {
          title: `1. ${i18n(dictionary.components.doneAdmissionModal.steps[1].title)}`,
          description: i18n(dictionary.components.doneAdmissionModal.steps[1].descriptionEsocial),
          icon: <SolutionOutlined />,
        },
        {
          title: `2. ${i18n(dictionary.components.doneAdmissionModal.steps[2].title)}`,
          description: i18n(dictionary.components.doneAdmissionModal.steps[2].description),
          icon: <ScheduleOutlined />,
        },
        {
          title: `3. ${i18n(dictionary.components.doneAdmissionModal.steps[3].title)}`,
          description: i18n(dictionary.components.doneAdmissionModal.steps[3].description),
          icon: <ScheduleOutlined />,
        },
        {
          title: `4. ${i18n(dictionary.components.doneAdmissionModal.steps[4].title)}`,
          description: i18n(dictionary.components.doneAdmissionModal.steps[4].description),
          icon: <ScheduleOutlined />,
        },
      ]
    : [
        {
          title: `1. ${i18n(dictionary.components.doneAdmissionModal.steps[1].title)}`,
          description: i18n(dictionary.components.doneAdmissionModal.steps[1].description),
          icon: <SolutionOutlined />,
        },
        {
          title: `2. ${i18n(dictionary.components.doneAdmissionModal.steps[4].title)}`,
          description: i18n(dictionary.components.doneAdmissionModal.steps[4].description),
          icon: <ScheduleOutlined />,
        },
      ];
  if (isYappUser) stepsItems.pop();

  return (
    <>
      {messageContextHolder}
      <Modal
        centered
        width={1500}
        styles={{
          content: { margin: 0, padding: 0 },
          body: {
            padding: 0,
          },
        }}
        open={open}
        onCancel={handleCancel}
        footer={false}
        maskClosable={false}
      >
        <Row style={{ minHeight: 600 }}>
          <Col span={7}>
            <Flex
              vertical
              style={{ padding: 24 }}
            >
              <Flex
                style={{ marginBottom: 24 }}
                align="flex-end"
              >
                <Image
                  src={LogoColoridaSimples}
                  preview={false}
                  width={80}
                />
                <Title
                  style={titleStyle}
                  level={5}
                >
                  {i18n(dictionary.components.doneAdmissionModal.name)}
                </Title>
              </Flex>
              <Steps
                direction="vertical"
                current={step - 1}
                items={stepsItems}
              />
            </Flex>
            <Flex
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
              }}
            >
              <Image
                style={{
                  borderBottomLeftRadius: 16,
                }}
                src={FirstAccessFlowDocumentation}
                preview={false}
              />
            </Flex>
          </Col>
          <Col
            span={17}
            style={{ background: "#F5F7FA", borderTopRightRadius: 16, borderBottomRightRadius: 16 }}
          >
            <Flex
              vertical
              style={{ padding: 24 }}
            >
              <Title
                style={titleStyle}
                level={3}
              >
                {sendToEsocial
                  ? i18n(dictionary.components.doneAdmissionModal.titleEsocial)
                  : i18n(dictionary.components.doneAdmissionModal.title)}
              </Title>
              <Flex
                vertical
                style={{ padding: 24, paddingBottom: 4 }}
              >
                <Title
                  style={{ fontWeight: 600 }}
                  type="secondary"
                  level={5}
                >
                  {sendToEsocial
                    ? `${i18n(dictionary.components.doneAdmissionModal.currentStep)}${step} / 4`
                    : `${i18n(dictionary.components.doneAdmissionModal.currentStep)}${step === 1 ? step : 2} / 2`}
                </Title>
                {step !== 1 && (
                  <Title
                    style={titleStyle}
                    level={4}
                  >
                    {i18n(dictionary.components.doneAdmissionModal.steps[step].title)}
                  </Title>
                )}

                <Flex style={{ margin: "24px 0" }}>
                  {/* Passo 1 */}
                  {step === 1 && (
                    <div style={{ maxHeight: "70vh", width: 1500, overflow: "auto", padding: "4px", marginTop: -16 }}>
                      <Spin
                        spinning={loadingData}
                        tip="Carregando formulário..."
                      >
                        {loadingData ? (
                          <Skeleton />
                        ) : (
                          <Form
                            form={formEsocial}
                            layout="vertical"
                            colon={false}
                            size="small"
                            scrollToFirstError
                          >
                            {/* Dados pessoais */}
                            <Title
                              style={titleStyle2}
                              level={4}
                            >
                              {i18n(dictionary.label.personal_data)}
                            </Title>
                            <div style={{ width: "100%", display: "flex", flexWrap: "wrap", columnGap: 8, rowGap: 0 }}>
                              {employee?.additionalData.employmentRelationship.relDocs.some(
                                (doc) => doc.document?.code === "GE" && doc.isRequired,
                              ) && (
                                <div
                                  key={"gender"}
                                  style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                >
                                  <Item
                                    label={i18n(dictionary.label.gender)}
                                    name="gender"
                                    rules={[
                                      { required: true, message: i18n(dictionary.message.requiredField) },
                                      { max: 1 },
                                    ]}
                                    hasFeedback
                                    validateFirst
                                  >
                                    <Select>
                                      {genders.map((gender) => (
                                        <Option
                                          key={gender.code}
                                          title={gender.name}
                                          value={gender.code}
                                        >
                                          {gender.name}
                                        </Option>
                                      ))}
                                    </Select>
                                  </Item>
                                </div>
                              )}

                              <div
                                key={"educationLevel"}
                                style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                              >
                                <Item
                                  label={i18n(dictionary.label.education_level)}
                                  name="educationLevel"
                                  rules={[{ required: true }, { max: 2 }]}
                                  hasFeedback
                                  validateFirst
                                >
                                  <Select>
                                    {educationLevel.map((level) => (
                                      <Option
                                        key={level.code}
                                        title={level.name}
                                        value={level.code}
                                      >
                                        {level.name}
                                      </Option>
                                    ))}
                                  </Select>
                                </Item>
                              </div>

                              <div
                                key={"raceId"}
                                style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                              >
                                <Item
                                  label={i18n(dictionary.label.race)}
                                  name="raceId"
                                  rules={[{ required: true }]}
                                  hasFeedback
                                  validateFirst
                                >
                                  <Select>
                                    {races.map((race) => (
                                      <Option
                                        key={race.id}
                                        title={race.name}
                                        value={race.id}
                                      >
                                        {race.name}
                                      </Option>
                                    ))}
                                  </Select>
                                </Item>
                              </div>

                              <div
                                key={"sex"}
                                style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                              >
                                <Item
                                  label={i18n(dictionary.label.sex)}
                                  name="sex"
                                  rules={[
                                    { required: true, message: i18n(dictionary.message.requiredField) },
                                    { max: 2 },
                                  ]}
                                  hasFeedback
                                  validateFirst
                                >
                                  <Select>
                                    {genders.map((gender) => (
                                      <Option
                                        key={gender.code}
                                        title={gender.name}
                                        value={gender.code}
                                      >
                                        {gender.name}
                                      </Option>
                                    ))}
                                  </Select>
                                </Item>
                              </div>

                              <div
                                key={"birthDate"}
                                style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                              >
                                <Item
                                  label={i18n(dictionary.label.birth_date)}
                                  name="birthDate"
                                  rules={[{ required: true }]}
                                  hasFeedback
                                  validateFirst
                                >
                                  <DatePicker format={[i18n(dictionary.dateFormat)]} />
                                </Item>
                              </div>

                              <div
                                key={"countryBirthId"}
                                style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                              >
                                <Item
                                  label={i18n(dictionary.label.country_of_birth)}
                                  name="countryBirthId"
                                  rules={[{ required: true }]}
                                  hasFeedback
                                  validateFirst
                                >
                                  <Select showSearch>
                                    {countryOfBirth.map((country) => (
                                      <Option
                                        key={country.id}
                                        title={country.name}
                                        value={country.id}
                                        label={country.name}
                                      >
                                        {country.name}
                                      </Option>
                                    ))}
                                  </Select>
                                </Item>
                              </div>

                              <div
                                key={"countryNationalityId"}
                                style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                              >
                                <Item
                                  label={i18n(dictionary.label.country_of_nationality)}
                                  name="countryNationalityId"
                                  rules={[{ required: true }]}
                                  hasFeedback
                                  validateFirst
                                >
                                  <Select showSearch>
                                    {countryOfNationality.map((country) => (
                                      <Option
                                        key={country.id}
                                        title={country.name}
                                        value={country.id}
                                      >
                                        {country.name}
                                      </Option>
                                    ))}
                                  </Select>
                                </Item>
                              </div>
                            </div>

                            {/* Documentos */}
                            <Title
                              style={titleStyle2}
                              level={4}
                            >
                              {i18n(dictionary.label.document, { count: 2 })}
                            </Title>
                            <div style={{ width: "100%", display: "flex", flexWrap: "wrap", gap: 8 }}>
                              {employee?.additionalData.employmentRelationship.relDocs.some(
                                (doc) => doc.document?.code === "RG" && doc.isRequired,
                              ) && (
                                <div
                                  key={"identity"}
                                  style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                >
                                  <Item
                                    label={i18n(dictionary.label.rg)}
                                    name="identity"
                                    rules={[{ required: true }, { max: 28 }]}
                                    hasFeedback
                                    validateFirst
                                  >
                                    <Input
                                      addonAfter={
                                        <DocumentMenuDropdown
                                          code="RG"
                                          employeeId={employee?.id ?? 0}
                                          url={urlUploadedFileIdentity}
                                        />
                                      }
                                    />
                                  </Item>
                                </div>
                              )}

                              {employee?.additionalData.employmentRelationship.relDocs.some(
                                (doc) => doc.document?.code === "CPF" && doc.isRequired,
                              ) && (
                                <div
                                  key={"cpf"}
                                  style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                >
                                  <Item
                                    label={i18n(dictionary.label.cpf)}
                                    name="cpf"
                                    rules={[{ required: true }, { max: 14 }]}
                                    hasFeedback
                                    validateFirst
                                  >
                                    <MaskedInput
                                      mask={"000.000.000-00"}
                                      value={cpf}
                                      defaultValue={cpf}
                                      addonAfter={
                                        <DocumentMenuDropdown
                                          code="CPF"
                                          employeeId={employee?.id ?? 0}
                                          url={urlUploadedFileCpf}
                                        />
                                      }
                                    />
                                  </Item>
                                </div>
                              )}

                              {employee?.additionalData.employmentRelationship.relDocs.some(
                                (doc) => doc.document?.code === "PIS" && doc.isRequired,
                              ) && (
                                <div
                                  key={"pisPasep"}
                                  style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                >
                                  <Item
                                    label={i18n(dictionary.label.pis_pasep)}
                                    name="pisPasep"
                                    rules={[{ required: true }, { max: 20 }]}
                                    hasFeedback
                                    validateFirst
                                  >
                                    <Input
                                      addonAfter={
                                        <DocumentMenuDropdown
                                          code="PIS"
                                          employeeId={employee?.id ?? 0}
                                          url={urlUploadedFilePis}
                                        />
                                      }
                                    />
                                  </Item>
                                </div>
                              )}

                              {employee?.additionalData.employmentRelationship.relDocs.some(
                                (doc) => doc.document?.code === "CT" && doc.isRequired,
                              ) && (
                                <div
                                  key={"workCardNumber"}
                                  style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                >
                                  <InputGroup style={{ display: "flex" }}>
                                    <Item
                                      label={i18n(dictionary.label.work_card_number)}
                                      name="workCardNumber"
                                      rules={[{ required: true }, { max: 20 }]}
                                      hasFeedback
                                      validateFirst
                                      style={{ width: "54%", marginRight: "4px" }}
                                    >
                                      <Input
                                        addonAfter={
                                          <DocumentMenuDropdown
                                            code="CT"
                                            employeeId={employee?.id ?? 0}
                                            url={urlUploadedFileCt}
                                          />
                                        }
                                      />
                                    </Item>
                                    <Item
                                      label={i18n(dictionary.label.work_card_serie)}
                                      name="workCardSerie"
                                      rules={[{ required: true }, { max: 255 }]}
                                      hasFeedback
                                      validateFirst
                                      style={{ width: "26%", marginRight: "4px" }}
                                    >
                                      <MaskedInput mask={[{ mask: numericRegExp }]} />
                                    </Item>
                                    <Item
                                      label={i18n(dictionary.label.work_card_state)}
                                      name="workCardState"
                                      rules={[{ required: true }]}
                                      hasFeedback
                                      validateFirst
                                      style={{ width: "20%" }}
                                    >
                                      <Select allowClear>
                                        {countryStates.map((countryState) => (
                                          <Option
                                            key={countryState.abbreviation}
                                            title={countryState.abbreviation}
                                            value={countryState.abbreviation}
                                          >
                                            {countryState.abbreviation}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </InputGroup>
                                </div>
                              )}

                              {employee?.additionalData.employmentRelationship.relDocs.some(
                                (doc) => doc.document?.code === "TE" && doc.isRequired,
                              ) && (
                                <div
                                  key={"voterRegistrationNumber"}
                                  style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                >
                                  <InputGroup style={{ display: "flex" }}>
                                    <Item
                                      label={i18n(dictionary.label.voter_registration_number)}
                                      name="voterRegistrationNumber"
                                      rules={[{ required: true }, { max: 30 }]}
                                      hasFeedback
                                      validateFirst
                                      style={{ width: "52%", marginRight: "4px" }}
                                    >
                                      <Input
                                        addonAfter={
                                          <DocumentMenuDropdown
                                            code="TE"
                                            employeeId={employee?.id ?? 0}
                                            url={urlUploadedFileTe}
                                          />
                                        }
                                      />
                                    </Item>

                                    <Item
                                      label={i18n(dictionary.label.voter_registration_section)}
                                      name="voterRegistrationSection"
                                      rules={[{ required: true }, { max: 255 }]}
                                      hasFeedback
                                      validateFirst
                                      style={{ width: "22%", marginRight: "4px" }}
                                    >
                                      <MaskedInput mask={[{ mask: numericRegExp }]} />
                                    </Item>
                                    <Item
                                      label={i18n(dictionary.label.voter_registration_zone)}
                                      name="voterRegistrationZone"
                                      rules={[{ required: true }, { max: 255 }]}
                                      hasFeedback
                                      validateFirst
                                      style={{ width: "26%" }}
                                    >
                                      <MaskedInput mask={[{ mask: numericRegExp }]} />
                                    </Item>
                                  </InputGroup>
                                </div>
                              )}

                              {employee?.additionalData.employmentRelationship.relDocs.some(
                                (doc) => doc.document?.code === "CNH" && doc.isRequired,
                              ) && (
                                <div
                                  key={"driverLicense"}
                                  style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                >
                                  <Item>
                                    <InputGroup>
                                      <Item
                                        label={i18n(dictionary.label.driver_license)}
                                        name="driverLicense"
                                        rules={[{ required: true }, { max: 255 }]}
                                        hasFeedback
                                        validateFirst
                                      >
                                        <Input
                                          addonAfter={
                                            <DocumentMenuDropdown
                                              code="CNH"
                                              employeeId={employee?.id ?? 0}
                                              url={urlUploadedFileCnh}
                                            />
                                          }
                                        />
                                      </Item>
                                      <Item
                                        label={i18n(dictionary.label.driver_license_expiration_date)}
                                        name="driverLicenseExpirationDate"
                                        rules={[{ required: true }]}
                                        hasFeedback
                                        validateFirst
                                      >
                                        <DatePicker format={[i18n(dictionary.dateFormat)]} />
                                      </Item>
                                    </InputGroup>
                                  </Item>
                                </div>
                              )}

                              {employee?.additionalData.employmentRelationship.relDocs.some(
                                (doc) => doc.document?.code === "NR" && doc.isRequired,
                              ) && (
                                <div
                                  key={"reservistNumber"}
                                  style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                >
                                  <InputGroup>
                                    <Item
                                      label={i18n(dictionary.label.reservist_number)}
                                      name="reservistNumber"
                                      rules={[{ required: true }, { max: 28 }]}
                                      hasFeedback
                                      validateFirst
                                      style={{ width: "80%", marginRight: "4px" }}
                                    >
                                      <Input
                                        addonAfter={
                                          <DocumentMenuDropdown
                                            code="NR"
                                            employeeId={employee?.id ?? 0}
                                            url={urlUploadedFileNr}
                                          />
                                        }
                                      />
                                    </Item>
                                    <Item
                                      label={i18n(dictionary.label.reservist_serie)}
                                      name="reservistSerie"
                                      rules={[{ required: true }, { max: 255 }]}
                                      hasFeedback
                                      validateFirst
                                      style={{ width: "20%", marginRight: "4px" }}
                                    >
                                      <Input />
                                    </Item>
                                  </InputGroup>
                                </div>
                              )}

                              {employee?.additionalData.employmentRelationship.relDocs.some(
                                (doc) => doc.document?.code === "CMEST" && doc.isRequired,
                              ) && (
                                <div
                                  key={"enrollmentEducation"}
                                  style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                >
                                  <Item
                                    label={i18n(dictionary.label.enrollmentEducation)}
                                    name="enrollmentEducation"
                                    rules={[{ required: true }, { max: 255 }]}
                                    hasFeedback
                                    validateFirst
                                  >
                                    <Input
                                      addonAfter={
                                        <DocumentMenuDropdown
                                          code="CMEST"
                                          employeeId={employee?.id ?? 0}
                                          url={urlUploadedFileCMEST}
                                        />
                                      }
                                    />
                                  </Item>
                                </div>
                              )}

                              {employee?.additionalData.employmentRelationship.relDocs.some(
                                (doc) => doc.document?.code === "TCE" && doc.isRequired,
                              ) && (
                                <div
                                  key={"internshipCommitmentTerm"}
                                  style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                >
                                  <Item
                                    label={i18n(dictionary.label.internshipCommitmentTerm)}
                                    name="internshipCommitmentTerm"
                                    rules={[{ required: true }, { max: 255 }]}
                                    hasFeedback
                                    validateFirst
                                  >
                                    <Input
                                      addonAfter={
                                        <DocumentMenuDropdown
                                          code="TCE"
                                          employeeId={employee?.id ?? 0}
                                          url={urlUploadedFileTce}
                                        />
                                      }
                                    />
                                  </Item>
                                </div>
                              )}

                              {(employee?.additionalData.employmentRelationship.relDocs.some(
                                (doc) => doc.document?.code === "CE" && doc.isRequired,
                              ) ||
                                sendToEsocial) && (
                                <>
                                  <div
                                    key={"publicPlace"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.address_street)}
                                      name="publicPlace"
                                      rules={[
                                        {
                                          required:
                                            sendToEsocial ||
                                            employee?.additionalData.employmentRelationship.relDocs.some(
                                              (doc) => doc.document?.code === "CE" && doc.isRequired,
                                            ),
                                          message: i18n(dictionary.message.requiredField),
                                        },
                                        { max: 255 },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Input
                                        addonAfter={
                                          <DocumentMenuDropdown
                                            code="CE"
                                            employeeId={employee?.id ?? 0}
                                            url={urlUploadedFileCe}
                                          />
                                        }
                                      />
                                    </Item>
                                  </div>
                                  <div
                                    key={"zipCode"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.address_zip_code)}
                                      name="zipCode"
                                      rules={[
                                        {
                                          required:
                                            sendToEsocial ||
                                            employee?.additionalData.employmentRelationship.relDocs.some(
                                              (doc) => doc.document?.code === "CE" && doc.isRequired,
                                            ),
                                          message: i18n(dictionary.message.requiredField),
                                        },
                                        { max: 9 },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <MaskedInput
                                        mask="00000-000"
                                        value={zipCode}
                                        defaultValue={zipCode}
                                      />
                                    </Item>
                                  </div>

                                  <div
                                    key={"addressNumber"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item>
                                      <InputGroup style={{ display: "flex" }}>
                                        <Item
                                          label={i18n(dictionary.label.address_number)}
                                          name="addressNumber"
                                          rules={[
                                            {
                                              required:
                                                sendToEsocial ||
                                                employee?.additionalData.employmentRelationship.relDocs.some(
                                                  (doc) => doc.document?.code === "CE" && doc.isRequired,
                                                ),
                                              message: i18n(dictionary.message.requiredField),
                                            },
                                            { max: 255 },
                                          ]}
                                          hasFeedback
                                          validateFirst
                                          style={{ width: "26%", marginRight: "4px" }}
                                        >
                                          <Input min={0} />
                                        </Item>
                                        <Item
                                          label={i18n(dictionary.label.address_complement)}
                                          name="complement"
                                          rules={[{ max: 255 }]}
                                          hasFeedback
                                          validateFirst
                                          style={{ width: "74%" }}
                                        >
                                          <Input />
                                        </Item>
                                      </InputGroup>
                                    </Item>
                                  </div>

                                  <div
                                    key={"zone"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.address_district)}
                                      name="zone"
                                      rules={[
                                        {
                                          required:
                                            sendToEsocial ||
                                            employee?.additionalData.employmentRelationship.relDocs.some(
                                              (doc) => doc.document?.code === "CE" && doc.isRequired,
                                            ),
                                          message: i18n(dictionary.message.requiredField),
                                        },
                                        { max: 255 },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Input />
                                    </Item>
                                  </div>

                                  <div
                                    key={"city"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.address_city)}
                                      name="city"
                                      rules={[
                                        {
                                          required:
                                            sendToEsocial ||
                                            employee?.additionalData.employmentRelationship.relDocs.some(
                                              (doc) => doc.document?.code === "CE" && doc.isRequired,
                                            ),
                                          message: i18n(dictionary.message.requiredField),
                                        },
                                        { max: 255 },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Input />
                                    </Item>
                                  </div>

                                  <div
                                    key={"state"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.address_state)}
                                      name="state"
                                      rules={[
                                        {
                                          required:
                                            sendToEsocial ||
                                            employee?.additionalData.employmentRelationship.relDocs.some(
                                              (doc) => doc.document?.code === "CE" && doc.isRequired,
                                            ),
                                          message: i18n(dictionary.message.requiredField),
                                        },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select allowClear>
                                        {countryStates.map((countryState) => (
                                          <Option
                                            key={countryState.abbreviation}
                                            title={countryState.name}
                                            value={countryState.abbreviation}
                                          >
                                            {countryState.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>
                                </>
                              )}
                            </div>

                            {/* Dados admissionais */}
                            <Title
                              style={titleStyle2}
                              level={4}
                            >
                              {i18n(dictionary.label.admissional_data)}
                            </Title>
                            <div style={{ width: "100%", display: "flex", flexWrap: "wrap", gap: 8 }}>
                              {sendToEsocial && (
                                <>
                                  {/* Dados do eSocial */}
                                  <div
                                    key="employmentRegimeId"
                                    style={{
                                      flex: "0 0 calc(33.333% - 8px)",
                                      minWidth: 0,
                                    }}
                                  >
                                    <Item
                                      label={capitalizeFirstLetter(i18n(dictionary.label.type_of_employment_regime))}
                                      name="employmentRegimeId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select>
                                        {laborRegime.map((regime) => (
                                          <Option
                                            key={regime.id}
                                            title={regime.name}
                                            value={regime.id}
                                          >
                                            {regime.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"pensionRegimeId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={capitalizeFirstLetter(i18n(dictionary.label.type_of_pension_scheme))}
                                      name="pensionRegimeId"
                                      rules={[{ required: true, message: i18n(dictionary.message.requiredField) }]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select>
                                        {pensionRegime.map((regime) => (
                                          <Option
                                            key={regime.id}
                                            title={regime.name}
                                            value={regime.id}
                                          >
                                            {regime.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"paymentUnitRemunerationId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={
                                        <>
                                          {capitalizeFirstLetter(i18n(dictionary.label.payment_unit))}
                                          <Tooltip
                                            title={capitalizeFirstLetter(
                                              i18n(dictionary.label.payment_unit_remuneration),
                                            )}
                                          >
                                            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                          </Tooltip>
                                        </>
                                      }
                                      name="paymentUnitRemunerationId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select
                                        onChange={(value) => {
                                          setHasSalaryDescription(value === 6 || value === 7);
                                        }}
                                      >
                                        {paymentUnitRemuneration.map((item) => (
                                          <Option
                                            key={item.id}
                                            title={item.name}
                                            value={item.id}
                                          >
                                            {item.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"salaryDescription"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={
                                        <>
                                          {i18n(dictionary.label.salary_description)}
                                          <Tooltip title={i18n(dictionary.message.salary_description)}>
                                            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                          </Tooltip>
                                        </>
                                      }
                                      name="salaryDescription"
                                      rules={[
                                        {
                                          required: hasSalaryDescription,
                                          message: i18n(dictionary.message.requiredField),
                                        },
                                        { max: 255 },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Input disabled={!hasSalaryDescription} />
                                    </Item>
                                  </div>

                                  <div
                                    key={"eSocialEventTypeId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={
                                        <>
                                          {i18n(dictionary.label.type_of_event_ESocial)}
                                          <Tooltip title={i18n(dictionary.message.type_of_event_ESocial)}>
                                            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                          </Tooltip>
                                        </>
                                      }
                                      name="eSocialEventTypeId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select>
                                        {typeEvent.map((item) => (
                                          <Option
                                            key={item.id}
                                            title={item.name}
                                            value={item.id}
                                          >
                                            {item.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"admissionTypeId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.admission_type)}
                                      name="admissionTypeId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select>
                                        {admissionType.map((item) => (
                                          <Option
                                            key={item.id}
                                            title={item.name}
                                            value={item.id}
                                          >
                                            {item.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"admissionIndicatorId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.admission_indicator)}
                                      name="admissionIndicatorId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select>
                                        {admissionIndicator.map((admissionIndicator) => (
                                          <Option
                                            key={admissionIndicator.id}
                                            title={admissionIndicator.name}
                                            value={admissionIndicator.id}
                                          >
                                            {admissionIndicator.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"workingDayRegimeId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.journey_regime)}
                                      name="workingDayRegimeId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select>
                                        {journeyRegime.map((journeyRegime) => (
                                          <Option
                                            key={journeyRegime.id}
                                            title={journeyRegime.name}
                                            value={journeyRegime.id}
                                          >
                                            {journeyRegime.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"eSocialActivityNatureId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.activity_nature)}
                                      name="eSocialActivityNatureId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select>
                                        {activityNature.map((activityNature) => (
                                          <Option
                                            key={activityNature.id}
                                            title={activityNature.name}
                                            value={activityNature.id}
                                          >
                                            {activityNature.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"monthYearId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={
                                        <>
                                          {i18n(dictionary.label.month_category)}
                                          <Tooltip title={i18n(dictionary.label.month_professional_category)}>
                                            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                          </Tooltip>
                                        </>
                                      }
                                      name="monthYearId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select>
                                        {monthProfessionalCategory.map((monthProfessionalCategory) => (
                                          <Option
                                            key={monthProfessionalCategory.id}
                                            title={monthProfessionalCategory.name}
                                            value={monthProfessionalCategory.id}
                                          >
                                            {monthProfessionalCategory.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"cnpjUnionRepresentative"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={
                                        <>
                                          {i18n(dictionary.label.cnpj_union_representative)}
                                          <Tooltip title={i18n(dictionary.label.cnpj_union_representative_tip)}>
                                            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                          </Tooltip>
                                        </>
                                      }
                                      name="cnpjUnionRepresentative"
                                      rules={[{ required: employmentRegimeId === 1 }, { max: 14 }, { min: 14 }]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <MaskedInput mask={[{ mask: numericRegExp }]} />
                                    </Item>
                                  </div>

                                  <div
                                    key={"fgtsDate"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.fgts_date)}
                                      required
                                      name="fgtsDate"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <DatePicker format={[i18n(dictionary.dateFormat)]} />
                                    </Item>
                                  </div>

                                  <div
                                    key={"isPositionJobCumulative"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item>
                                      <InputGroup>
                                        <Item
                                          label={i18n(dictionary.label.is_position_job_cumulative)}
                                          name="isPositionJobCumulative"
                                          hasFeedback
                                          validateFirst
                                        >
                                          <Row>
                                            <Switch
                                              checkedChildren={i18n(dictionary.label.yes)}
                                              unCheckedChildren={i18n(dictionary.label.no)}
                                              onChange={(checked: boolean) => {
                                                formEsocial.setFieldsValue({ isPositionJobCumulative: checked });
                                                setIsPositionJobCumulative(checked);
                                              }}
                                              checked={isPositionJobCumulative}
                                            />
                                          </Row>
                                        </Item>
                                      </InputGroup>
                                    </Item>
                                  </div>

                                  <div
                                    key={"legalHypothesisTemporaryWorkerId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.data_on_temporary_work)}
                                      name="legalHypothesisTemporaryWorkerId"
                                      rules={[
                                        {
                                          required: isTemporaryCollaborator,
                                          message: i18n(dictionary.message.requiredField),
                                        },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select disabled={!isTemporaryCollaborator}>
                                        {legalHypothesisTemporaryWorker.map((legalHypothesisTemporaryWorker) => (
                                          <Option
                                            key={legalHypothesisTemporaryWorker.id}
                                            title={legalHypothesisTemporaryWorker.name}
                                            value={legalHypothesisTemporaryWorker.id}
                                          >
                                            {legalHypothesisTemporaryWorker.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"cnpjCpfTemporaryEmployee"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={
                                        <>
                                          {i18n(dictionary.label.cnpj_cpf_temporary)}
                                          <Tooltip title={i18n(dictionary.label.cnpj_cpf_temporary_employee)}>
                                            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                          </Tooltip>
                                        </>
                                      }
                                      name="cnpjCpfTemporaryEmployee"
                                      rules={[
                                        {
                                          required: isTemporaryCollaborator,
                                          message: i18n(dictionary.message.requiredField),
                                        },
                                        { max: 14 },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <MaskedInput
                                        disabled={!isTemporaryCollaborator}
                                        mask={[{ mask: numericRegExp }]}
                                      />
                                    </Item>
                                  </div>

                                  <div
                                    key={"employmentContractTypeId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.type_employment_contract)}
                                      name="employmentContractTypeId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select onChange={typeEmploymentContractOnchange}>
                                        {typeEmploymentContract.map((typeEmploymentContract) => (
                                          <Option
                                            key={typeEmploymentContract.id}
                                            title={typeEmploymentContract.name}
                                            value={typeEmploymentContract.id}
                                          >
                                            {typeEmploymentContract.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"endContractDate"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.end_contract_date)}
                                      name="endContractDate"
                                      rules={[
                                        { required: isTpContrDay, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <DatePicker
                                        format={[i18n(dictionary.dateFormat)]}
                                        disabled={!(isTpContrDay || isTpContrFact)}
                                      />
                                    </Item>
                                  </div>

                                  <div
                                    key={"assuranceClause"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={
                                        <>
                                          {i18n(dictionary.label.clause)}
                                          <Tooltip title={i18n(dictionary.label.assurance_clause)}>
                                            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                          </Tooltip>
                                        </>
                                      }
                                      name="assuranceClause"
                                      rules={[
                                        { required: isTpContrDay, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Switch
                                        checkedChildren={i18n(dictionary.label.yes)}
                                        unCheckedChildren={i18n(dictionary.label.no)}
                                        onChange={(checked: boolean) => {
                                          formEsocial.setFieldsValue({ hasAssuranceClause: checked });
                                          setHasAssuranceClause(checked);
                                        }}
                                        checked={hasAssuranceClause}
                                        disabled={!isTpContrDay}
                                      />
                                    </Item>
                                  </div>

                                  <div
                                    key={"determiningObject"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={
                                        <>
                                          {i18n(dictionary.label.determining)}
                                          <Tooltip title={i18n(dictionary.label.determining_object)}>
                                            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                          </Tooltip>
                                        </>
                                      }
                                      name="determiningObject"
                                      rules={[{ required: isTpContrFact }, { max: 255 }]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Input disabled={!isTpContrFact} />
                                    </Item>
                                  </div>

                                  <div
                                    key={"workerEstablishmentId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={
                                        <>
                                          {i18n(dictionary.label.establishment)}
                                          <Tooltip
                                            title={
                                              i18n(dictionary.label.worker_establishment) +
                                              ". " +
                                              i18n(dictionary.message.worker_establishment)
                                            }
                                          >
                                            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                          </Tooltip>
                                        </>
                                      }
                                      name="workerEstablishmentId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select>
                                        {registrationType.map((registrationTyp) => (
                                          <Option
                                            key={registrationTyp.id}
                                            title={registrationTyp.name}
                                            value={registrationTyp.id}
                                          >
                                            {registrationTyp.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"registrationNumber"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={
                                        <Row>
                                          {i18n(dictionary.label.registration_number)}
                                          <Tooltip title={i18n(dictionary.message.registration_number)}>
                                            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                          </Tooltip>
                                        </Row>
                                      }
                                      name="registrationNumber"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                        { max: 14 },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <MaskedInput mask={[{ mask: numericRegExp }]} />
                                    </Item>
                                  </div>

                                  <div
                                    key={"workload"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.workload)}
                                      name="workload"
                                      rules={[{ required: sendToEsocial }, { type: "number", min: 0, max: 9999 }]}
                                      initialValue={0}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <InputNumber
                                        addonAfter={
                                          <Item
                                            name="workloadType"
                                            noStyle
                                          >
                                            <Select
                                              options={[
                                                {
                                                  value: 1,
                                                  label: i18n(dictionary.label.weekly),
                                                },
                                                {
                                                  value: 2,
                                                  label: i18n(dictionary.label.monthly),
                                                },
                                              ]}
                                            />
                                          </Item>
                                        }
                                        controls={false}
                                      />
                                    </Item>
                                  </div>

                                  <div
                                    key={"workingDayTypeId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.type_of_working_day)}
                                      name="workingDayTypeId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select>
                                        {typeOfWorkingDay.map((typeOfWorkingDay) => (
                                          <Option
                                            key={typeOfWorkingDay.id}
                                            title={typeOfWorkingDay.name}
                                            value={typeOfWorkingDay.id}
                                          >
                                            {typeOfWorkingDay.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"partTimeContractId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.is_part_time_contract)}
                                      name="partTimeContractId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select>
                                        {partTimeContract.map((partTimeContract) => (
                                          <Option
                                            key={partTimeContract.id}
                                            title={partTimeContract.name}
                                            value={partTimeContract.id}
                                          >
                                            {partTimeContract.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>

                                  <div
                                    key={"hasWeeklyShiftNightTime"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.weekly_shift_nighttime)}
                                      name="hasWeeklyShiftNightTime"
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Row>
                                        <Switch
                                          checkedChildren={i18n(dictionary.label.yes)}
                                          unCheckedChildren={i18n(dictionary.label.no)}
                                          onChange={(checked: boolean) => {
                                            formEsocial.setFieldsValue({ hasWeeklyShiftNightTime: checked });
                                            setHasWeeklyShiftNighttime(checked);
                                          }}
                                          checked={hasWeeklyShiftNighttime}
                                        />
                                      </Row>
                                    </Item>
                                  </div>

                                  <div
                                    key={"descriptionContractualWeeklyShift"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)" }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.description_contractual_weekly_shift)}
                                      name="descriptionContractualWeeklyShift"
                                      rules={[{ required: sendToEsocial }, { max: 255 }]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Input placeholder="Horário de trabalho: Segunda à sexta/ Folga: Sábado e..." />
                                    </Item>
                                  </div>

                                  <div
                                    key={"workerCategoryId"}
                                    style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                                  >
                                    <Item
                                      label={i18n(dictionary.label.employee_category_code)}
                                      name="workerCategoryId"
                                      rules={[
                                        { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                      ]}
                                      hasFeedback
                                      validateFirst
                                    >
                                      <Select showSearch>
                                        {workerCategory.map((category) => (
                                          <Option
                                            key={category.name}
                                            title={`${category.code} – ${category.name}`}
                                            value={category.id}
                                          >
                                            {category.code} – {category.name}
                                          </Option>
                                        ))}
                                      </Select>
                                    </Item>
                                  </div>
                                </>
                              )}

                              <div
                                key={"registration"}
                                style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                              >
                                <Item
                                  label={i18n(dictionary.label.registry)}
                                  name="registration"
                                  rules={[{ required: sendToEsocial }, { max: 50 }]}
                                  hasFeedback
                                  validateFirst
                                >
                                  <MaskedInput
                                    placeholder={i18n(dictionary.insert, {
                                      item: i18n(dictionary.label.registry),
                                    })}
                                    mask={[{ mask: numericRegExp }]}
                                    onPaste={handlePaste}
                                  />
                                </Item>
                              </div>

                              <div
                                key={"cboIdentifier"}
                                style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                              >
                                <Item
                                  label={
                                    <Row>
                                      {i18n(dictionary.label.job_occupation_register)}
                                      <Tooltip title={i18n(dictionary.message.job_occupation_register)}>
                                        <QuestionCircleOutlined style={{ marginLeft: 10 }} />
                                      </Tooltip>
                                    </Row>
                                  }
                                  name="cboIdentifier"
                                  rules={[
                                    { required: sendToEsocial, message: i18n(dictionary.message.requiredField) },
                                    { max: 255 },
                                  ]}
                                  hasFeedback
                                  validateFirst
                                >
                                  <Select
                                    showSearch
                                    onSelect={(_: any, option: DefaultOptionType) =>
                                      formEsocial.setFieldsValue({
                                        jobOccupation: String(option.key),
                                      })
                                    }
                                  >
                                    {jobOccupations.map((job) => (
                                      <Option
                                        key={job.name}
                                        title={`${job.code} – ${job.name}`}
                                        value={job.code}
                                      >
                                        {job.code} – {job.name}
                                      </Option>
                                    ))}
                                  </Select>
                                </Item>
                              </div>

                              <div
                                key={"admissionDate"}
                                style={{ flex: "0 0 calc(33.333% - 8px)", minWidth: 0 }}
                              >
                                <Item
                                  label={i18n(dictionary.label.admission_date)}
                                  name="admissionDate"
                                  rules={[{ required: sendToEsocial, message: i18n(dictionary.message.requiredField) }]}
                                  hasFeedback
                                  validateFirst
                                >
                                  <DatePicker format={[i18n(dictionary.dateFormat)]} />
                                </Item>
                              </div>
                            </div>
                          </Form>
                        )}
                      </Spin>
                    </div>
                  )}

                  {/* Passo 2 - Senha do certificado digital */}
                  {step === 2 && (
                    <Form
                      form={formCertificatePassword}
                      layout="vertical"
                      name="basic"
                      labelCol={{ span: 8 }}
                      wrapperCol={{ span: 16 }}
                      style={{ maxWidth: 600, marginTop: 8, minHeight: 420 }}
                      autoComplete="off"
                      onFinish={formPassHandleSubmit}
                    >
                      <Item
                        name="password"
                        label={i18n(dictionary.components.doneAdmissionModal.steps[2].form.inputPassword)}
                        rules={[{ required: true, message: "Por favor informe a senha do certificado!" }]}
                      >
                        <Input.Password placeholder="Digite aqui" />
                      </Item>
                      <Typography.Text type="danger">
                        {i18n(dictionary.info.E_SOCIAL_ALMOST_THERE.message)}
                      </Typography.Text>
                    </Form>
                  )}

                  {/* Passo 3 - Processamento do lote*/}
                  {step === 3 && (
                    <Form
                      layout="vertical"
                      form={formEsocialMonitoring}
                      style={{ width: 400, minHeight: 420 }}
                    >
                      <Item label={i18n(dictionary.components.doneAdmissionModal.steps[3].form.monitor)}>
                        <Progress
                          showInfo={false}
                          percent={
                            progressObject[progressIndex].status === "active"
                              ? percent
                              : progressObject[progressIndex].percent
                          }
                          status={progressObject[progressIndex].status as ProgressStatuses}
                        />
                      </Item>
                      <Typography.Text
                        type={progressIndex === 0 ? "warning" : progressIndex === 1 ? "danger" : "success"}
                      >
                        {progressIndex === 0
                          ? i18n(dictionary.components.doneAdmissionModal.steps[3].form.status.inProgress)
                          : progressIndex === 1
                          ? errorContent
                          : i18n(dictionary.components.doneAdmissionModal.steps[3].form.status.success)}
                      </Typography.Text>
                    </Form>
                  )}

                  {/* Passo 4 - Parametrizar controle de ponto */}
                  {step === 4 && !isYappUser && (
                    <Form
                      layout="vertical"
                      form={formTimesheetConfig}
                    >
                      {/*Data de início*/}
                      <Item
                        name={"timesheetStartDate"}
                        label={i18n(dictionary.components.doneAdmissionModal.steps[4].form.startDate.label)}
                      >
                        <DatePicker
                          placeholder={i18n(
                            dictionary.components.doneAdmissionModal.steps[4].form.startDate.placeholder,
                          )}
                          style={{ width: "100%" }}
                          format={"DD/MM/YYYY"}
                        />
                      </Item>
                      {/*Local de trabalho*/}
                      <Item
                        name={"workplaceId"}
                        label={i18n(dictionary.components.doneAdmissionModal.steps[4].form.workplace.label)}
                      >
                        <Select
                          options={workplaces.map((element) => ({
                            value: element.id,
                            label: element.description,
                          }))}
                          loading={loadingData}
                          disabled={loadingData}
                          placeholder={i18n(
                            dictionary.components.doneAdmissionModal.steps[4].form.workplace.placeholder,
                          )}
                        />
                      </Item>
                      {/*Escala de trabalho*/}
                      <Item
                        name={"scheduleId"}
                        label={i18n(dictionary.components.doneAdmissionModal.steps[4].form.schedule.label)}
                      >
                        <Select
                          options={schedules.map((element) => ({
                            value: element.id,
                            label: element.description,
                          }))}
                          loading={loadingData}
                          disabled={loadingData}
                          placeholder={i18n(
                            dictionary.components.doneAdmissionModal.steps[4].form.schedule.placeholder,
                          )}
                        />
                      </Item>
                      {/*Regra de horas*/}
                      <Item
                        name={"timeRuleId"}
                        label={i18n(dictionary.components.doneAdmissionModal.steps[4].form.timeRule.label)}
                      >
                        <Select
                          options={timeRules.map((element) => ({
                            value: element.id,
                            label: element.description,
                          }))}
                          loading={loadingData}
                          disabled={loadingData}
                          placeholder={i18n(
                            dictionary.components.doneAdmissionModal.steps[4].form.timeRule.placeholder,
                          )}
                        />
                      </Item>
                      {/*PIN de acesso*/}
                      <Item
                        name={"pin"}
                        label={i18n(dictionary.components.doneAdmissionModal.steps[4].form.pin)}
                      >
                        <InputNumber
                          min={"0"}
                          max="99999"
                          style={{ width: "100%" }}
                          controls={inputNumberControls}
                          addonAfter={
                            <KeyOutlined
                              title={i18n(dictionary.components.doneAdmissionModal.steps[4].form.pin)}
                              onClick={() => {
                                gerarPin();
                              }}
                              style={{ cursor: "pointer" }}
                            />
                          }
                          maxLength={5}
                          value={pin}
                          onKeyUp={async () => {
                            localDebounce(async () => {
                              await checkPin(pin);
                            }, 600);
                          }}
                          onBlur={async ({ target }) => {
                            await checkPin(pin);
                          }}
                        />
                      </Item>
                      <Item
                        label={i18n(dictionary.components.doneAdmissionModal.steps[4].form.others.label)}
                        name="timeRecordProofEmail"
                        valuePropName="checked"
                      >
                        <Checkbox>
                          {i18n(dictionary.components.doneAdmissionModal.steps[4].form.others.options.proof)}
                        </Checkbox>
                      </Item>
                      <Item
                        name="webAccess"
                        valuePropName="checked"
                      >
                        <Checkbox>
                          {i18n(dictionary.components.doneAdmissionModal.steps[4].form.others.options.webAccess)}
                        </Checkbox>
                      </Item>
                      <Item
                        name="invite"
                        valuePropName="checked"
                      >
                        <Checkbox>
                          {i18n(dictionary.components.doneAdmissionModal.steps[4].form.others.options.invite)}
                        </Checkbox>
                      </Item>
                    </Form>
                  )}
                </Flex>
              </Flex>
              {/* Botões de ação */}
              <Flex gap={10}>
                {step !== 1 && step !== 4 && (
                  <Button
                    icon={<LeftOutlined />}
                    onClick={() => {
                      if (sendToEsocial) {
                        setStep((step) => (step - 1) as Step);
                      } else {
                        setStep(1);
                      }
                    }}
                  >
                    {i18n(dictionary.action.back)}
                  </Button>
                )}
                <Button
                  type="primary"
                  icon={<RightOutlined />}
                  onClick={handleOk}
                  loading={finishing}
                  disabled={step === 3 && !sendEsocialSuccess}
                >
                  {i18n(
                    dictionary.components.doneAdmissionModal.steps[
                      step === 1 && isYappUser && !sendToEsocial ? 4 : step
                    ].button,
                  )}
                </Button>
              </Flex>
            </Flex>
          </Col>
        </Row>
      </Modal>
    </>
  );
}
